import KanbanAPI from '../../api/kanban';

const DEFAULT_PER_PAGE = 30;

const extractKanbanResponse = data => {
  const payload = data?.data?.payload || data?.payload || [];
  const meta = data?.data?.meta || data?.meta || {};
  return {
    payload: Array.isArray(payload) ? payload : [],
    meta,
  };
};

const state = {
  funnels: [],
  currentFunnel: null,
  stages: [],
  conversationsByStage: {},
  stagePagination: {},
  uiFlags: {
    isFetchingFunnels: false,
    isFetchingStages: false,
    isFetchingConversations: false,
    isMovingConversation: false,
  },
};

const getters = {
  getFunnels: state => state.funnels,
  getCurrentFunnel: state => state.currentFunnel,
  getStages: state => state.stages,
  getConversationsByStage: state => stageId => state.conversationsByStage[stageId] || [],
  getStagePagination: state => stageId =>
    state.stagePagination[stageId] || {
      currentPage: 0,
      totalPages: 0,
      totalCount: 0,
      perPage: DEFAULT_PER_PAGE,
      hasMore: false,
      isLoading: false,
    },
  getUIFlags: state => state.uiFlags,
  
  getStageById: state => id => state.stages.find(s => s.id === id),
  
  getTotalConversations: state => {
    return Object.values(state.conversationsByStage).reduce((sum, convs) => sum + convs.length, 0);
  },
};

const actions = {
  // Funnels
  async fetchFunnels({ commit }, params = {}) {
    commit('setUIFlag', { key: 'isFetchingFunnels', value: true });
    try {
      const { data } = await KanbanAPI.getFunnels(params);
      commit('setFunnels', data);
      return data;
    } finally {
      commit('setUIFlag', { key: 'isFetchingFunnels', value: false });
    }
  },

  async fetchFunnel({ commit }, funnelId) {
    commit('setUIFlag', { key: 'isFetchingStages', value: true });
    try {
      const { data } = await KanbanAPI.getFunnel(funnelId);
      
      
      commit('setCurrentFunnel', data);
      commit('setStages', data.kanban_stages || data.stages || []);
      return data;
    } catch (error) {
      console.error('Error fetching funnel:', error);
      // Em caso de erro, não limpar os stages existentes
      throw error;
    } finally {
      commit('setUIFlag', { key: 'isFetchingStages', value: false });
    }
  },

  async createFunnel({ commit, dispatch }, funnelData) {
    const { data } = await KanbanAPI.createFunnel(funnelData);
    dispatch('fetchFunnels');
    return data;
  },

  async updateFunnel({ commit }, { funnelId, data }) {
    const response = await KanbanAPI.updateFunnel(funnelId, data);
    commit('updateFunnel', response.data);
    return response.data;
  },

  async deleteFunnel({ commit }, funnelId) {
    await KanbanAPI.deleteFunnel(funnelId);
    commit('removeFunnel', funnelId);
  },

  // Stages
  async fetchStages({ commit }, funnelId) {
    commit('setUIFlag', { key: 'isFetchingStages', value: true });
    try {
      const { data } = await KanbanAPI.getStages(funnelId);
      commit('setStages', data);
      return data;
    } finally {
      commit('setUIFlag', { key: 'isFetchingStages', value: false });
    }
  },

  async createStage({ commit, state }, stageData) {
    if (!state.currentFunnel?.id) {
      throw new Error('No current funnel selected');
    }
    
    const { data } = await KanbanAPI.createStage(state.currentFunnel.id, stageData);
    commit('addStage', data);
    return data;
  },

  async updateStage({ commit, state }, { stageId, data }) {
    if (!state.currentFunnel?.id) {
      throw new Error('No current funnel selected');
    }
    const response = await KanbanAPI.updateStage(state.currentFunnel.id, stageId, data);
    commit('updateStage', response.data);
    return response.data;
  },

  async deleteStage({ commit, state }, stageId) {
    if (!state.currentFunnel?.id) {
      throw new Error('No current funnel selected');
    }
    await KanbanAPI.deleteStage(state.currentFunnel.id, stageId);
    commit('removeStage', stageId);
  },

  async reorderStages({ commit, state }, stageIds) {
    if (!state.currentFunnel?.id) {
      throw new Error('No current funnel selected');
    }
    const { data } = await KanbanAPI.reorderStages(state.currentFunnel.id, stageIds);
    commit('setStages', data);
  },

  // Conversations
  async fetchStageConversations(
    { commit },
    { stageId, params = {}, skipLoading = false }
  ) {
    if (!skipLoading) {
      commit('setUIFlag', { key: 'isFetchingConversations', value: true });
    }

    try {
      const { data } = await KanbanAPI.getStageConversations(stageId, params);
      const { payload, meta } = extractKanbanResponse(data);
      commit('setStageConversations', {
        stageId,
        conversations: payload,
        meta,
      });
      return data;
    } finally {
      if (!skipLoading) {
        commit('setUIFlag', { key: 'isFetchingConversations', value: false });
      }
    }
  },

  async fetchAllStagesConversations({ commit, dispatch, state }) {
    if (!state.stages.length) return;

    commit('setUIFlag', { key: 'isFetchingConversations', value: true });
    try {
      const promises = state.stages.map(stage =>
        dispatch('fetchStageConversations', {
          stageId: stage.id,
          params: { per_page: DEFAULT_PER_PAGE, page: 1 },
          skipLoading: true,
        })
      );
      await Promise.all(promises);
    } finally {
      commit('setUIFlag', { key: 'isFetchingConversations', value: false });
    }
  },

  async moveConversation(
    { commit, dispatch },
    { conversationId, conversationDisplayId, fromStageId, toStageId, reason }
  ) {
    commit('setUIFlag', { key: 'isMovingConversation', value: true });
    try {
      // Optimistic update
      commit('moveConversationOptimistic', { conversationId, fromStageId, toStageId });
      
      const conversationReferenceId = conversationDisplayId || conversationId;
      const { data } = await KanbanAPI.moveConversation(
        conversationReferenceId,
        toStageId,
        reason
      );
      return data;
    } catch (error) {
      // Rollback on error when source stage is known. Otherwise refresh all columns.
      if (fromStageId) {
        commit('moveConversationOptimistic', {
          conversationId,
          fromStageId: toStageId,
          toStageId: fromStageId,
        });
      } else {
        await dispatch('fetchAllStagesConversations');
      }
      throw error;
    } finally {
      commit('setUIFlag', { key: 'isMovingConversation', value: false });
    }
  },

  syncConversationCard({ commit }, conversation) {
    commit('syncConversationCard', conversation);
  },

  async loadMoreStageConversations({ commit, state }, { stageId }) {
    if (!stageId) return;

    const currentPagination = state.stagePagination[stageId] || {};
    const hasMore = Boolean(currentPagination.hasMore);
    const isLoading = Boolean(currentPagination.isLoading);
    const currentPage = Number(currentPagination.currentPage || 1);

    if (isLoading || !hasMore) return;

    commit('setStagePaginationLoading', { stageId, isLoading: true });
    try {
      const nextPage = currentPage + 1;
      const { data } = await KanbanAPI.getStageConversations(stageId, {
        page: nextPage,
        per_page: DEFAULT_PER_PAGE,
      });
      const { payload, meta } = extractKanbanResponse(data);
      commit('appendStageConversations', {
        stageId,
        conversations: payload,
        meta,
      });
    } finally {
      commit('setStagePaginationLoading', { stageId, isLoading: false });
    }
  },
};

const mutations = {
  setFunnels(state, funnels) {
    state.funnels = funnels;
  },

  setCurrentFunnel(state, funnel) {
    state.currentFunnel = funnel;
  },

  updateFunnel(state, funnel) {
    const index = state.funnels.findIndex(f => f.id === funnel.id);
    if (index !== -1) {
      state.funnels[index] = funnel;
    }
    if (state.currentFunnel?.id === funnel.id) {
      state.currentFunnel = funnel;
    }
  },

  removeFunnel(state, funnelId) {
    state.funnels = state.funnels.filter(f => f.id !== funnelId);
  },

  setStages(state, stages) {
    const normalizedStages = Array.isArray(stages) ? stages : [];
    state.stages = normalizedStages;
    const stageIds = new Set(normalizedStages.map(stage => stage.id));

    state.conversationsByStage = Object.fromEntries(
      Object.entries(state.conversationsByStage).filter(([stageId]) =>
        stageIds.has(Number(stageId))
      )
    );

    state.stagePagination = Object.fromEntries(
      Object.entries(state.stagePagination).filter(([stageId]) =>
        stageIds.has(Number(stageId))
      )
    );

    normalizedStages.forEach(stage => {
      if (!state.conversationsByStage[stage.id]) {
        state.conversationsByStage[stage.id] = [];
      }
      if (!state.stagePagination[stage.id]) {
        state.stagePagination[stage.id] = {
          currentPage: 0,
          totalPages: 0,
          totalCount: 0,
          perPage: DEFAULT_PER_PAGE,
          hasMore: false,
          isLoading: false,
        };
      }
    });
  },

  addStage(state, stage) {
    // Verificar se o stage já existe para evitar duplicações
    const existingIndex = state.stages.findIndex(s => s.id === stage.id);
    if (existingIndex === -1) {
      state.stages.push(stage);
      state.conversationsByStage[stage.id] = [];
    } else {
      // Se já existe, atualizar
      state.stages[existingIndex] = stage;
    }
  },

  updateStage(state, stage) {
    const index = state.stages.findIndex(s => s.id === stage.id);
    if (index !== -1) {
      state.stages[index] = stage;
    }
  },

  removeStage(state, stageId) {
    state.stages = state.stages.filter(s => s.id !== stageId);
    delete state.conversationsByStage[stageId];
    delete state.stagePagination[stageId];
  },

  setStageConversations(state, { stageId, conversations, meta = {} }) {
    const currentPage = Number(meta.current_page || 1);
    const totalPages = Number(meta.total_pages || 1);
    const resolvedHasMore =
      meta.has_more !== undefined ? Boolean(meta.has_more) : currentPage < totalPages;

    state.conversationsByStage[stageId] = conversations;
    state.stagePagination[stageId] = {
      ...(state.stagePagination[stageId] || {}),
      currentPage,
      totalPages,
      totalCount: Number(meta.total_count || conversations.length || 0),
      perPage: Number(meta.per_page || DEFAULT_PER_PAGE),
      hasMore: resolvedHasMore,
      isLoading: false,
    };
  },

  appendStageConversations(state, { stageId, conversations, meta = {} }) {
    const existingConversations = state.conversationsByStage[stageId] || [];
    const existingIds = new Set(existingConversations.map(conversation => conversation.id));
    const dedupedIncomingConversations = conversations.filter(
      conversation => !existingIds.has(conversation.id)
    );

    state.conversationsByStage[stageId] = [
      ...existingConversations,
      ...dedupedIncomingConversations,
    ];

    const currentPage = Number(
      meta.current_page || state.stagePagination[stageId]?.currentPage || 1
    );
    const totalPages = Number(
      meta.total_pages || state.stagePagination[stageId]?.totalPages || 1
    );
    const resolvedHasMore =
      meta.has_more !== undefined ? Boolean(meta.has_more) : currentPage < totalPages;

    state.stagePagination[stageId] = {
      ...(state.stagePagination[stageId] || {}),
      currentPage,
      totalPages,
      totalCount: Number(meta.total_count || state.stagePagination[stageId]?.totalCount || 0),
      perPage: Number(meta.per_page || state.stagePagination[stageId]?.perPage || DEFAULT_PER_PAGE),
      hasMore: resolvedHasMore,
      isLoading: false,
    };
  },

  setStagePaginationLoading(state, { stageId, isLoading }) {
    state.stagePagination[stageId] = {
      ...(state.stagePagination[stageId] || {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        perPage: DEFAULT_PER_PAGE,
        hasMore: false,
      }),
      isLoading,
    };
  },

  syncConversationCard(state, conversationPayload) {
    if (!conversationPayload?.id) return;

    const payloadId = Number(conversationPayload.id);
    if (!Number.isInteger(payloadId)) return;

    Object.keys(state.conversationsByStage).forEach(stageId => {
      state.conversationsByStage[stageId] = state.conversationsByStage[stageId].map(
        conversation => {
          if (Number(conversation.id) !== payloadId) return conversation;

          return {
            ...conversation,
            ...conversationPayload,
            meta: {
              ...(conversation.meta || {}),
              ...(conversationPayload.meta || {}),
            },
          };
        }
      );
    });
  },

  moveConversationOptimistic(state, { conversationId, fromStageId, toStageId }) {
    let sourceStageId = fromStageId;

    if (!sourceStageId) {
      sourceStageId = Object.keys(state.conversationsByStage).find(stageId =>
        state.conversationsByStage[stageId].some(c => c.id === conversationId)
      );
    }

    if (!sourceStageId || !state.conversationsByStage[sourceStageId]) {
      return;
    }

    const conversation = state.conversationsByStage[sourceStageId].find(
      c => c.id === conversationId
    );
    state.conversationsByStage[sourceStageId] = state.conversationsByStage[
      sourceStageId
    ].filter(c => c.id !== conversationId);

    if (!conversation) return;

    if (!state.conversationsByStage[toStageId]) {
      state.conversationsByStage[toStageId] = [];
    }

    state.conversationsByStage[toStageId] = state.conversationsByStage[
      toStageId
    ].filter(c => c.id !== conversationId);

    state.conversationsByStage[toStageId].unshift({
      ...conversation,
      kanban_stage_id: toStageId,
      kanban_stage: {
        ...(conversation.kanban_stage || {}),
        id: toStageId,
      },
    });

    if (state.stagePagination[sourceStageId]) {
      state.stagePagination[sourceStageId] = {
        ...state.stagePagination[sourceStageId],
        totalCount: Math.max(
          0,
          Number(state.stagePagination[sourceStageId].totalCount || 0) - 1
        ),
      };
    }

    if (state.stagePagination[toStageId]) {
      state.stagePagination[toStageId] = {
        ...state.stagePagination[toStageId],
        totalCount: Number(state.stagePagination[toStageId].totalCount || 0) + 1,
      };
    }
  },

  setUIFlag(state, { key, value }) {
    state.uiFlags[key] = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
